import React, { useState, useEffect } from "react";
// import { useNavigate, useLocation } from "react-router-dom"; 
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
    FiFilter, FiGrid, FiList, FiStar, FiShoppingCart, FiHeart,
    FiPlus, FiMinus, FiSearch, FiX, FiTrendingUp, FiPackage,
    FiTag, FiDollarSign, FiAward, FiPercent, FiChevronLeft,
    FiChevronRight, FiChevronsLeft, FiChevronsRight
} from "react-icons/fi";
import { useCart } from "../App";
import ProductCard from "./ProductCard";

const API_BASE_URL = "https://server.havbit.in/api";
const PRODUCTS_API = `${API_BASE_URL}/public/products`;
const CATEGORIES_API = `${API_BASE_URL}/categories`;
const SUBCATEGORIES_API = `${API_BASE_URL}/subcategories`;

// ✅ Helper: create URL-friendly slug from product name
const createSlug = (name) => {
    if (!name) return '';
    return name
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
        .replace(/(^-|-$)/g, '');
};

// ✅ Helper: remove timestamp suffix from API slug (e.g. -1770373161491)
const cleanApiSlug = (slug) => {
    if (!slug) return '';
    return slug.replace(/-\d{10,}$/, '');
};

// ✅ Helper: get best slug for a product
const getProductSlug = (product) => {
    if (product.slug) return cleanApiSlug(product.slug);
    return createSlug(product.name);
};

export default function Products() {
    const navigate = useNavigate();
    const location = useLocation(); 
    const { categorySlug } = useParams();
    const { cart, addToCart, updateQty, removeFromCart, wishlist = [], toggleWishlistItem } = useCart();

    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [viewMode, setViewMode] = useState("grid");
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedCategory, setSelectedCategory] = useState("all");
    const [priceRange, setPriceRange] = useState([0, 5000]);
    const [sortBy, setSortBy] = useState("newest");
    const [showMobileFilters, setShowMobileFilters] = useState(false);
    const [subCategories, setSubCategories] = useState([]);
    const [selectedSubCategory, setSelectedSubCategory] = useState("all");
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(40);
    const [selectedBrand, setSelectedBrand] = useState("all"); // ✅ ADD: brand filter state

    const cartQuantities = cart.reduce((acc, item) => {
        acc[item._id] = item.qty || 1;
        return acc;
    }, {});

    const isProductInWishlist = (productId) => wishlist.some(item => item._id === productId);

    useEffect(() => { fetchData(); }, []);

    // ✅ ADD: URL se brand param read karo (products load hone ke baad)
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const brandParam = params.get("brand");
        if (brandParam) {
            setSelectedBrand(decodeURIComponent(brandParam));
            setSearchTerm(decodeURIComponent(brandParam)); // search mein bhi dikh jaye
        }
    }, [location.search, products]);
    useEffect(() => {
    if (!categorySlug || categories.length === 0) return;

    const matchedCategory = categories.find(
        (cat) =>
            cat.toLowerCase().replace(/\s+/g, "-") ===
            categorySlug.toLowerCase()
    );

    if (matchedCategory) {
        setSelectedCategory(matchedCategory);
        setCurrentPage(1);
    }
}, [categorySlug, categories]);

    const fetchData = async () => {
        try {
            setLoading(true);
            const [productsRes, categoriesRes, subcategoriesRes] = await Promise.all([
                fetch(PRODUCTS_API),
                fetch(CATEGORIES_API),
                fetch(SUBCATEGORIES_API)
            ]);

            if (!productsRes.ok) throw new Error("Failed to fetch products");

            const productsResult = await productsRes.json();
            const categoriesData = categoriesRes.ok ? await categoriesRes.json() : [];

            const categoriesMap = {};
            categoriesData.forEach(cat => {
                if (cat._id && cat.name) categoriesMap[cat._id] = cat.name;
            });

            const subcategoriesMap = {};
            let subcategoriesData = [];
            try {
                const subcategoriesResult = await subcategoriesRes.json();
                subcategoriesData = subcategoriesResult.data || [];
                subcategoriesData.forEach(sc => {
                    if (sc._id && sc.name) subcategoriesMap[sc._id] = sc.name;
                });
            } catch (e) { /* fallback */ }

            const productsArray = productsResult.data || [];
            const categorySet = new Set(["all"]);
            const subCategorySet = new Set(["all"]);
            let maxPrice = 0;

            const formattedProducts = productsArray.map(product => {
                let categoryName = "";
                if (product.category && categoriesMap[product.category]) {
                    categoryName = categoriesMap[product.category];
                } else if (product.categoryName) {
                    categoryName = product.categoryName;
                }

                let subcategoryName = "";
                if (product.subcategoryName) {
                    subcategoryName = product.subcategoryName;
                } else if (product.subcategory && typeof product.subcategory === "string") {
                    if (subcategoriesMap[product.subcategory]) {
                        subcategoryName = subcategoriesMap[product.subcategory];
                    } else if (product.subcategory.length !== 24) {
                        subcategoryName = product.subcategory.trim();
                    }
                }

                if (categoryName.trim()) categorySet.add(categoryName);
                if (subcategoryName.trim()) subCategorySet.add(subcategoryName);

                const price = Number(product.price || product.newPrice || 0);
                const originalPrice = Number(product.oldPrice || price * 1.2 || 0);
                if (price > maxPrice) maxPrice = price;
                const discount = (originalPrice > 0 && price > 0 && originalPrice > price)
                    ? Math.round(((originalPrice - price) / originalPrice) * 100) : 0;

                let imageUrl = product.image;
                if (!imageUrl && product.gallery?.length > 0) imageUrl = product.gallery[0];

                const resolvedVendorId =
                    product.vendorId ||
                    product.vendor?._id ||
                    null;

                const isVendorProduct =
                    typeof product.isVendorProduct === "boolean"
                        ? product.isVendorProduct
                        : resolvedVendorId !== null;

                return {
                    ...product,
                    id: product._id || product.id,
                    slug: getProductSlug(product),
                    name: product.name || "Unnamed Product",
                    price,
                    originalPrice,
                    discount,
                    savings: Math.max(0, originalPrice - price),
                    image: imageUrl || "https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=400",
                    category: categoryName || "General",
                    subcategory: subcategoryName || "",
                    stock: Number(product.stock || 0),
                    weight: product.itemWeight || product.customWeight || product.size || "100g",
                    description: product.description || "",
                    restaurantName: product.restaurantName || product.brandName || "",
                    vendor: product.vendor || null,
                    vendorId: resolvedVendorId,
                    isVendorProduct,
                    quality: product.quality || "Good",
                    rating: parseFloat((Math.random() * 1.5 + 3.5).toFixed(1)),
                    reviewCount: Math.floor(Math.random() * 1000) + 50,
                    deliveryTime: `${Math.floor(Math.random() * 20) + 10} mins`,
                    brandName: product.brandName || "",
                    createdAt: product.createdAt || new Date().toISOString()
                };
            });

            const validCategories = Array.from(categorySet).filter(c => c && c.trim()).sort();
            const validSubCategories = Array.from(subCategorySet).filter(sc => sc && sc.trim()).sort();

            setCategories(validCategories);
            setSubCategories(validSubCategories);
            setProducts(formattedProducts);

            const roundedMax = Math.ceil(maxPrice / 100) * 100;
            setPriceRange([0, Math.max(roundedMax, 1000)]);
            setLoading(false);
        } catch (error) {
            console.error("Error fetching data:", error);
            setLoading(false);
        }
    };

    const handleAddToCart = (product, quantity = 1) => {
        const cartItem = {
            _id: product.id || product._id,
            name: product.name,
            price: product.price,
            originalPrice: product.originalPrice,
            image: product.image,
            category: product.category,
            subcategory: product.subcategory,
            weight: product.weight,
            restaurantName: product.restaurantName,
            brandName: product.brandName,
            vendorId: product.vendorId || null,
            vendor: product.vendor || null,
            isVendorProduct: product.isVendorProduct,
            qty: quantity
        };

        addToCart(cartItem, quantity);
        window.dispatchEvent(new CustomEvent("showNotification", {
            detail: { message: `${product.name} added to cart!`, type: "success" }
        }));
    };

    const handleQuantityUpdate = (productId, change) => {
        const currentQty = cartQuantities[productId] || 0;
        const newQty = currentQty + change;
        if (newQty < 1) removeFromCart(productId);
        else updateQty(productId, newQty);
    };

    const handleToggleWishlist = (product) => {
        toggleWishlistItem({
            _id: product.id,
            name: product.name,
            price: product.price,
            originalPrice: product.originalPrice,
            image: product.image,
            category: product.category,
            subcategory: product.subcategory,
            weight: product.weight,
            savings: product.savings,
            discount: product.discount,
            vendorId: product.vendorId || null,
            restaurantName: product.restaurantName,
            brandName: product.brandName
        });
        window.dispatchEvent(new CustomEvent("showNotification", {
            detail: {
                message: isProductInWishlist(product.id) ? "Removed from wishlist" : "Added to wishlist!",
                type: "info"
            }
        }));
    };

    const filteredProducts = products.filter(product => {
        const matchesSearch = searchTerm === "" ||
            product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (product.description && product.description.toLowerCase().includes(searchTerm.toLowerCase())) ||
            product.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (product.subcategory && product.subcategory.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (product.restaurantName && product.restaurantName.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (product.brandName && product.brandName.toLowerCase().includes(searchTerm.toLowerCase()));
        const matchesCategory = selectedCategory === "all" || product.category === selectedCategory;
        const matchesSubCategory = selectedSubCategory === "all" || product.subcategory === selectedSubCategory;
        const matchesPrice = product.price >= priceRange[0] && product.price <= priceRange[1];
        return matchesSearch && matchesCategory && matchesSubCategory && matchesPrice;
    });

    const sortedProducts = [...filteredProducts].sort((a, b) => {
        switch (sortBy) {
            case "price-low": return a.price - b.price;
            case "price-high": return b.price - a.price;
            case "discount": return (b.discount || 0) - (a.discount || 0);
            case "rating": return b.rating - a.rating;
            case "newest": return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
            case "stock": return b.stock - a.stock;
            default: return 0;
        }
    });

    const filteredSavings = sortedProducts.reduce((t, p) => t + (p.savings > 0 ? p.savings : 0), 0);

    const totalItems = sortedProducts.length;
    const totalPages = Math.ceil(totalItems / itemsPerPage);
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = sortedProducts.slice(indexOfFirstItem, indexOfLastItem);

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
        window.scrollTo({ top: 0, behavior: "smooth" });
    };
    const handleNextPage = () => { if (currentPage < totalPages) { setCurrentPage(p => p + 1); window.scrollTo({ top: 0, behavior: "smooth" }); } };
    const handlePrevPage = () => { if (currentPage > 1) { setCurrentPage(p => p - 1); window.scrollTo({ top: 0, behavior: "smooth" }); } };

    const getPageNumbers = () => {
        const pages = [];
        const max = 5;
        if (totalPages <= max) {
            for (let i = 1; i <= totalPages; i++) pages.push(i);
        } else {
            let start = Math.max(2, currentPage - 1);
            let end = Math.min(totalPages - 1, currentPage + 1);
            if (currentPage <= 2) end = 4;
            else if (currentPage >= totalPages - 1) start = totalPages - 3;
            pages.push(1);
            if (start > 2) pages.push("...");
            for (let i = start; i <= end; i++) pages.push(i);
            if (end < totalPages - 1) pages.push("...");
            if (totalPages > 1) pages.push(totalPages);
        }
        return pages;
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white pb-24">
                <div className="container mx-auto px-4 py-6">
                    <div className="flex flex-col items-center justify-center h-64">
                        <div className="w-10 h-10 border-4 border-red-200 border-t-red-600 rounded-full animate-spin mb-3"></div>
                        <p className="text-sm text-gray-600">Loading products...</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white pb-24">

            {/* Mobile Header */}
            <div className="lg:hidden bg-white shadow-md sticky top-0 z-40 border-b border-gray-200">
                <div className="container mx-auto px-3 py-3">
                    <div className="flex items-center justify-between mb-3">
                        <div className="flex-1">
                            <h1 className="text-lg font-bold text-gray-900">All Products</h1>
                            <p className="text-xs text-gray-500">{products.length} premium products</p>
                        </div>
                        <button onClick={() => navigate("/")} className="flex items-center gap-1 text-gray-700 hover:text-red-600">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                            </svg>
                            <span className="text-xs font-medium">Home</span>
                        </button>
                    </div>
                    <div className="relative">
                        <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={14} />
                        <input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
                            placeholder="Search products..." className="w-full pl-9 pr-7 py-2 bg-gray-50 border border-gray-200 rounded-lg text-xs focus:ring-1 focus:ring-red-500 focus:border-transparent" />
                        {searchTerm && <button onClick={() => setSearchTerm("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"><FiX size={14} /></button>}
                    </div>
                </div>
            </div>

            {/* Desktop Header */}
            <div className="hidden lg:block bg-white shadow-sm border-b border-gray-200">
                <div className="container mx-auto px-6 py-4">
                    <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-4">
                            <button onClick={() => navigate("/")} className="flex items-center gap-2 text-gray-700 hover:text-red-600 transition-colors group">
                                <div className="w-8 h-8 rounded-full bg-gray-100 group-hover:bg-red-100 flex items-center justify-center transition-colors">
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                                    </svg>
                                </div>
                                <span className="text-sm font-medium">Back to Home</span>
                            </button>
                            <div className="w-px h-4 bg-gray-300"></div>
                            <div>
                                <h1 className="text-lg font-bold text-gray-900">All Products</h1>
                                <p className="text-xs text-gray-600">{products.length} premium products</p>
                            </div>
                        </div>
                        {filteredSavings > 0 && (
                            <div className="text-right">
                                <p className="text-xs text-gray-600">Total Savings</p>
                                <p className="text-sm font-bold text-green-600">₹{filteredSavings.toLocaleString("en-IN")}</p>
                            </div>
                        )}
                    </div>
                    <div className="relative max-w-2xl">
                        <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                        <input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
                            placeholder="Search for products, categories..."
                            className="w-full pl-12 pr-10 py-3 bg-white border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-red-500 focus:border-transparent shadow-sm" />
                        {searchTerm && <button onClick={() => setSearchTerm("")} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"><FiX size={16} /></button>}
                    </div>
                </div>
            </div>

            <div className="container mx-auto px-3 lg:px-6 py-3 lg:py-6">
                <div className="flex flex-col lg:flex-row gap-4 lg:gap-6">

                    {/* Desktop Sidebar Filters */}
                    <div className="hidden lg:block lg:w-1/4">
                        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sticky top-24">

                            <div className="mb-6">
                                <div className="flex items-center justify-between mb-3">
                                    <h3 className="font-bold text-gray-900 flex items-center gap-2 text-sm"><FiPackage size={16} /> Categories</h3>
                                    {selectedCategory !== "all" && <button onClick={() => setSelectedCategory("all")} className="text-xs text-red-600 hover:text-red-700">Reset</button>}
                                </div>
                                <div className="space-y-1.5 max-h-60 overflow-y-auto pr-2">
                                    <button onClick={() => { setSelectedCategory("all"); setCurrentPage(1); }}
                                        className={`w-full text-left px-3 py-2 rounded-lg text-xs transition-all flex items-center gap-2 ${selectedCategory === "all" ? "bg-gradient-to-r from-red-600 to-red-500 text-white font-medium shadow" : "text-gray-700 hover:bg-gray-50 hover:text-red-600"}`}>
                                        <FiTrendingUp size={14} /> All Products
                                    </button>
                                    {categories.map(category => (
                                        <button key={category} onClick={() => { setSelectedCategory(category); setCurrentPage(1); }}
                                            className={`w-full text-left px-3 py-2 rounded-lg text-xs transition-all flex items-center justify-between ${selectedCategory === category ? "bg-red-50 text-red-700 font-medium border border-red-200" : "text-gray-700 hover:bg-gray-50 hover:text-red-600"}`}>
                                            <span className="flex items-center gap-2"><FiPackage size={12} />{category}</span>
                                            {selectedCategory === category && <FiTrendingUp className="text-red-500" size={12} />}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {subCategories.length > 1 && subCategories.some(sc => sc !== "all" && sc.trim()) && (
                                <div className="mb-6">
                                    <div className="flex items-center justify-between mb-3">
                                        <h3 className="font-bold text-gray-900 flex items-center gap-2 text-sm"><FiTag size={14} /> Sub-Categories</h3>
                                        {selectedSubCategory !== "all" && <button onClick={() => setSelectedSubCategory("all")} className="text-xs text-red-600 hover:text-red-700">Reset</button>}
                                    </div>
                                    <div className="space-y-1.5 max-h-48 overflow-y-auto pr-2">
                                        {subCategories.filter(sc => sc && sc.trim()).slice(0, 12).map(subCat => (
                                            <button key={subCat} onClick={() => { setSelectedSubCategory(subCat); setCurrentPage(1); }}
                                                className={`w-full text-left px-3 py-1.5 rounded-lg text-xs transition-all ${selectedSubCategory === subCat ? "bg-red-50 text-red-700 font-medium border border-red-200" : "text-gray-700 hover:bg-gray-50 hover:text-red-600"}`}>
                                                {subCat === "all" ? "All Sub-Categories" : subCat}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <div className="mb-6">
                                <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2 text-sm"><FiDollarSign size={14} /> Price Range</h3>
                                <div className="space-y-2 px-1">
                                    <div className="flex items-center justify-between">
                                        <span className="text-xs text-gray-600">₹{priceRange[0]}</span>
                                        <span className="text-xs text-gray-600">₹{priceRange[1]}</span>
                                    </div>
                                    <input type="range" min="0" max={priceRange[1]} step="100" value={priceRange[1]}
                                        onChange={e => { setPriceRange([priceRange[0], parseInt(e.target.value)]); setCurrentPage(1); }}
                                        className="w-full h-1.5 bg-gray-200 rounded-full appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-red-600 [&::-webkit-slider-thumb]:shadow" />
                                </div>
                            </div>

                            <div className="mb-6">
                                <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2 text-sm"><FiFilter size={14} /> Sort By</h3>
                                <div className="space-y-1.5">
                                    {[
                                        { value: "newest", label: "Newest First", icon: FiAward },
                                        { value: "price-low", label: "Price: Low to High", icon: FiTrendingUp },
                                        { value: "price-high", label: "Price: High to Low", icon: FiTrendingUp },
                                        { value: "discount", label: "Best Discount", icon: FiPercent },
                                        { value: "rating", label: "Top Rated", icon: FiStar },
                                        { value: "stock", label: "Most Stock", icon: FiPackage }
                                    ].map(option => {
                                        const Icon = option.icon;
                                        return (
                                            <label key={option.value} className="flex items-center gap-2.5 cursor-pointer p-1.5 rounded-lg hover:bg-gray-50">
                                                <input type="radio" name="sort" value={option.value} checked={sortBy === option.value}
                                                    onChange={e => { setSortBy(e.target.value); setCurrentPage(1); }}
                                                    className="w-3.5 h-3.5 text-red-600" />
                                                <span className="text-xs text-gray-700 flex items-center gap-1.5"><Icon size={12} />{option.label}</span>
                                            </label>
                                        );
                                    })}
                                </div>
                            </div>

                            <button onClick={() => { setSelectedCategory("all"); setSelectedSubCategory("all"); setPriceRange([0, 5000]); setSortBy("newest"); setSearchTerm(""); setCurrentPage(1); }}
                                className="w-full py-2.5 text-white bg-gradient-to-r from-red-600 to-red-500 rounded-lg font-medium text-xs hover:shadow-md transition-all">
                                Clear All Filters
                            </button>
                        </div>
                    </div>

                    {/* Products Section */}
                    <div className="lg:w-3/4">
                        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-3 mb-4">
                            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                                <div className="flex-1">
                                    <h2 className="text-base font-bold text-gray-900">
                                        {selectedCategory === "all" ? "All Products" : selectedCategory}
                                        {selectedSubCategory !== "all" && ` (${selectedSubCategory})`}
                                    </h2>
                                    <div className="flex items-center gap-3 mt-1">
                                        <p className="text-xs text-gray-600">
                                            Showing {indexOfFirstItem + 1}–{Math.min(indexOfLastItem, totalItems)} of {totalItems} products
                                        </p>
                                        {filteredSavings > 0 && <p className="text-xs font-bold text-green-600">Save ₹{filteredSavings.toLocaleString("en-IN", { maximumFractionDigits: 0 })}</p>}
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    <button onClick={() => setShowMobileFilters(true)}
                                        className="lg:hidden flex items-center gap-1.5 px-3 py-2 text-gray-700 bg-gray-100 border border-gray-300 rounded-lg text-xs hover:bg-gray-200">
                                        <FiFilter size={12} /> Filters
                                    </button>
                                    <div className="hidden lg:flex items-center gap-1 bg-gray-100 rounded-lg p-0.5">
                                        {[{ mode: "grid", Icon: FiGrid }, { mode: "list", Icon: FiList }].map(({ mode, Icon }) => (
                                            <button key={mode} onClick={() => setViewMode(mode)}
                                                className={`p-1.5 rounded-md transition-all ${viewMode === mode ? "bg-gradient-to-r from-red-600 to-red-500 text-white shadow" : "text-gray-600 hover:text-red-600 hover:bg-gray-200"}`}>
                                                <Icon size={14} />
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {currentItems.length === 0 ? (
                            <div className="text-center py-8 bg-white rounded-xl shadow-sm border border-gray-200">
                                <div className="w-14 h-14 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-3">
                                    <FiSearch className="text-red-600 text-lg" />
                                </div>
                                <h3 className="text-sm font-bold text-gray-900 mb-1.5">No products found</h3>
                                <p className="text-xs text-gray-600 mb-5 max-w-sm mx-auto">
                                    {searchTerm ? `No products matching "${searchTerm}"` : "Try adjusting your filters."}
                                </p>
                                <button onClick={() => { setSearchTerm(""); setSelectedCategory("all"); setSelectedSubCategory("all"); setPriceRange([0, 5000]); setCurrentPage(1); }}
                                    className="px-4 py-2 bg-gradient-to-r from-red-600 to-red-500 text-white font-medium rounded-lg text-xs hover:shadow-md transition-all">
                                    Reset Filters
                                </button>
                            </div>
                        ) : (
                            <>
                                <div className={`${viewMode === "list" ? "hidden lg:block space-y-3" : "grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 lg:gap-4"}`}>
                                    <AnimatePresence>
                                        {currentItems.map(product => (
                                            <ProductCard
                                                key={product.id}
                                                product={product}
                                                onAddToCart={handleAddToCart}
                                                onToggleWishlist={handleToggleWishlist}
                                                onQuantityUpdate={handleQuantityUpdate}
                                                isWishlisted={isProductInWishlist(product.id)}
                                                cartQuantity={cartQuantities[product.id] || 0}
                                                navigate={navigate}
                                                viewMode={viewMode}
                                            />
                                        ))}
                                    </AnimatePresence>
                                </div>

                                {totalPages > 1 && (
                                    <div className="mt-6 bg-white rounded-xl shadow-sm border border-gray-200 p-4">
                                        <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
                                            <div className="text-xs text-gray-600">
                                                Page {currentPage} of {totalPages} • {Math.min(itemsPerPage, currentItems.length)} of {totalItems} products
                                            </div>
                                            <div className="flex items-center gap-1">
                                                <button onClick={() => handlePageChange(1)} disabled={currentPage === 1} className="p-2 rounded-lg text-gray-600 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"><FiChevronsLeft size={14} /></button>
                                                <button onClick={handlePrevPage} disabled={currentPage === 1} className="p-2 rounded-lg text-gray-600 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"><FiChevronLeft size={14} /></button>
                                                <div className="flex items-center gap-1">
                                                    {getPageNumbers().map((page, idx) => page === "..." ? (
                                                        <span key={`e-${idx}`} className="px-2 text-gray-400">...</span>
                                                    ) : (
                                                        <button key={page} onClick={() => handlePageChange(page)}
                                                            className={`w-8 h-8 flex items-center justify-center rounded-lg text-xs font-medium transition-all ${currentPage === page ? "bg-gradient-to-r from-red-600 to-red-500 text-white shadow" : "text-gray-700 hover:bg-gray-100 hover:text-red-600"}`}>
                                                            {page}
                                                        </button>
                                                    ))}
                                                </div>
                                                <button onClick={handleNextPage} disabled={currentPage === totalPages} className="p-2 rounded-lg text-gray-600 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"><FiChevronRight size={14} /></button>
                                                <button onClick={() => handlePageChange(totalPages)} disabled={currentPage === totalPages} className="p-2 rounded-lg text-gray-600 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"><FiChevronsRight size={14} /></button>
                                            </div>
                                            <div className="text-xs text-gray-600">Items per page: <span className="font-medium text-gray-900">{itemsPerPage}</span></div>
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </div>

            {/* Mobile Filters Modal */}
            <AnimatePresence>
                {showMobileFilters && (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 lg:hidden">
                        <div className="absolute inset-0 bg-black bg-opacity-50 backdrop-blur-sm" onClick={() => setShowMobileFilters(false)} />
                        <motion.div initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }} transition={{ type: "spring", damping: 25 }}
                            className="absolute bottom-0 left-0 right-0 bg-white rounded-t-xl max-h-[85vh] overflow-y-auto">
                            <div className="sticky top-0 bg-white z-10 border-b border-gray-200">
                                <div className="p-3 flex items-center justify-between">
                                    <h2 className="text-lg font-bold text-gray-900">Filters & Sorting</h2>
                                    <button onClick={() => setShowMobileFilters(false)} className="w-7 h-7 rounded-full bg-gray-100 flex items-center justify-center text-gray-700 hover:text-red-600"><FiX size={16} /></button>
                                </div>
                            </div>
                            <div className="p-3">
                                <div className="mb-4">
                                    <h3 className="font-bold text-gray-900 mb-2 text-sm">Categories</h3>
                                    <div className="grid grid-cols-2 gap-1.5">
                                        <button onClick={() => { setSelectedCategory("all"); setCurrentPage(1); }} className={`px-3 py-2 rounded-lg text-xs flex items-center justify-center gap-1.5 ${selectedCategory === "all" ? "bg-gradient-to-r from-red-600 to-red-500 text-white shadow" : "bg-gray-100 text-gray-700"}`}><FiTrendingUp size={12} /> All</button>
                                        {categories.slice(0, 6).map(category => (
                                            <button key={category} onClick={() => { setSelectedCategory(category); setCurrentPage(1); }} className={`px-3 py-2 rounded-lg text-xs ${selectedCategory === category ? "bg-gradient-to-r from-red-600 to-red-500 text-white shadow" : "bg-gray-100 text-gray-700"}`}>{category}</button>
                                        ))}
                                    </div>
                                </div>

                                {subCategories.length > 1 && subCategories.some(sc => sc !== "all" && sc.trim()) && (
                                    <div className="mb-4">
                                        <h3 className="font-bold text-gray-900 mb-2 text-sm">Sub-Categories</h3>
                                        <div className="grid grid-cols-2 gap-1.5">
                                            {subCategories.filter(sc => sc && sc.trim()).slice(0, 6).map(subCat => (
                                                <button key={subCat} onClick={() => { setSelectedSubCategory(subCat); setCurrentPage(1); }} className={`px-3 py-2 rounded-lg text-xs ${selectedSubCategory === subCat ? "bg-gradient-to-r from-red-600 to-red-500 text-white shadow" : "bg-gray-100 text-gray-700"}`}>
                                                    {subCat === "all" ? "All" : subCat}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                <div className="mb-4">
                                    <div className="flex items-center justify-between mb-2">
                                        <h3 className="font-bold text-gray-900 text-sm">Price Range</h3>
                                        <span className="text-xs font-bold text-red-600">₹{priceRange[0]} - ₹{priceRange[1]}</span>
                                    </div>
                                    <input type="range" min="0" max={priceRange[1]} step="100" value={priceRange[1]}
                                        onChange={e => { setPriceRange([priceRange[0], parseInt(e.target.value)]); setCurrentPage(1); }}
                                        className="w-full h-1.5 bg-gray-300 rounded-full appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-red-600 [&::-webkit-slider-thumb]:shadow" />
                                    <div className="flex items-center justify-between text-[10px] text-gray-600 mt-1">
                                        <span>₹0</span><span>₹{priceRange[1]}</span>
                                    </div>
                                </div>

                                <div className="mb-6">
                                    <h3 className="font-bold text-gray-900 mb-2 text-sm">Sort By</h3>
                                    <div className="space-y-1.5">
                                        {[
                                            { value: "newest", label: "Newest First" }, { value: "price-low", label: "Price: Low to High" },
                                            { value: "price-high", label: "Price: High to Low" }, { value: "discount", label: "Best Discount" },
                                            { value: "rating", label: "Top Rated" }, { value: "stock", label: "Most Stock" }
                                        ].map(option => (
                                            <button key={option.value} onClick={() => { setSortBy(option.value); setCurrentPage(1); }}
                                                className={`w-full text-left px-3 py-2 rounded-lg text-xs flex items-center justify-between ${sortBy === option.value ? "bg-gradient-to-r from-red-600 to-red-500 text-white shadow" : "bg-gray-100 text-gray-700"}`}>
                                                <span className="font-medium">{option.label}</span>
                                                {sortBy === option.value && <div className="w-4 h-4 rounded-full bg-white flex items-center justify-center"><div className="w-1.5 h-1.5 rounded-full bg-red-600"></div></div>}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                <div className="sticky bottom-0 bg-white pt-3 border-t border-gray-200 pb-3">
                                    <button onClick={() => setShowMobileFilters(false)} className="w-full py-3 bg-gradient-to-r from-red-600 to-red-500 text-white font-bold text-sm rounded-lg shadow hover:shadow-red-300 transition-all">
                                        Show {currentItems.length} Products
                                    </button>
                                </div>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}